/** Providers that only serve market info (CoinGecko: coin data; EIA, BLS, FRED: economic calendar results). */
const INFO_ONLY = ['coingecko', 'eia', 'bls', 'fred'];
export const ROUTE_CLASSES = ['CRYPTO', 'FX', 'INDEX', 'COMMODITY', 'STOCK'];
export const ROUTE_CLASS_LABEL = { CRYPTO: 'Crypto', FX: 'FX', INDEX: 'Indices', COMMODITY: 'Commodities', STOCK: 'US stocks' };
const NO_PLAN = { perMin: null, perDay: null, perMonth: null };
export const PROVIDERS = [
    {
        id: 'binance',
        label: 'Binance',
        usedFor: 'Crypto',
        keyless: true,
        fields: [],
        classes: ['CRYPTO'],
        defaultRestPerMin: 60,
        defaultStreamSlots: null,
        signupUrl: null,
        defaultPlan: NO_PLAN,
    },
    {
        id: 'kraken',
        label: 'Kraken',
        usedFor: 'Crypto backup · FX majors',
        keyless: true,
        fields: [],
        classes: ['CRYPTO', 'FX'],
        defaultRestPerMin: null,
        defaultStreamSlots: null,
        signupUrl: null,
        defaultPlan: NO_PLAN,
    },
    {
        id: 'oanda',
        label: 'OANDA',
        usedFor: 'FX · Indices · Commodities',
        keyless: false,
        fields: [
            { name: 'token', label: 'API token', secret: true, required: true, placeholder: 'Personal access token' },
            { name: 'accountId', label: 'Account ID', secret: false, required: true, placeholder: '101-004-1234567-001' },
            {
                name: 'environment',
                label: 'Environment',
                secret: false,
                required: true,
                options: [
                    { value: 'practice', label: 'Demo' },
                    { value: 'live', label: 'Live' },
                ],
            },
        ],
        classes: ['FX', 'INDEX', 'COMMODITY'],
        defaultRestPerMin: 100,
        defaultStreamSlots: null,
        signupUrl: 'https://www.oanda.com/demo-account/tpa/personal_token',
        defaultPlan: NO_PLAN,
    },
    {
        id: 'alpaca',
        label: 'Alpaca',
        usedFor: 'US stocks',
        keyless: false,
        fields: [
            { name: 'keyId', label: 'API key ID', secret: true, required: true },
            { name: 'secretKey', label: 'Secret key', secret: true, required: true },
            {
                name: 'feed',
                label: 'Data feed',
                secret: false,
                required: true,
                options: [
                    { value: 'iex', label: 'IEX (free)' },
                    { value: 'sip', label: 'SIP (paid)' },
                ],
            },
        ],
        classes: ['STOCK'],
        defaultRestPerMin: 150,
        defaultStreamSlots: 30,
        signupUrl: 'https://app.alpaca.markets/signup',
        defaultPlan: NO_PLAN,
    },
    {
        id: 'finnhub',
        label: 'Finnhub',
        usedFor: 'US stocks · News',
        keyless: false,
        fields: [{ name: 'apiKey', label: 'API key', secret: true, required: true }],
        classes: ['STOCK', 'FX', 'INDEX', 'COMMODITY'],
        defaultRestPerMin: 30,
        defaultStreamSlots: 50,
        signupUrl: 'https://finnhub.io/register',
        defaultPlan: { perMin: 60, perDay: null, perMonth: null },
    },
    {
        id: 'twelvedata',
        label: 'Twelve Data',
        usedFor: 'Chart history · Backup',
        keyless: false,
        fields: [{ name: 'apiKey', label: 'API key', secret: true, required: true }],
        classes: ['STOCK', 'FX', 'INDEX', 'COMMODITY', 'CRYPTO'],
        defaultRestPerMin: 6,
        defaultStreamSlots: 8,
        signupUrl: 'https://twelvedata.com/register',
        defaultPlan: { perMin: 8, perDay: 800, perMonth: null },
    },
    {
        id: 'coingecko',
        label: 'CoinGecko',
        usedFor: 'Market info',
        keyless: true,
        fields: [{ name: 'apiKey', label: 'Demo API key (optional)', secret: true, required: false }],
        classes: [],
        defaultRestPerMin: null,
        defaultStreamSlots: null,
        signupUrl: 'https://www.coingecko.com/en/developers/dashboard',
        defaultPlan: { perMin: null, perDay: null, perMonth: 10_000 },
    },
    {
        id: 'eia',
        label: 'EIA',
        usedFor: 'Calendar · Oil and gas results',
        keyless: false,
        fields: [{ name: 'apiKey', label: 'API key', secret: true, required: true }],
        classes: [],
        defaultRestPerMin: null,
        defaultStreamSlots: null,
        signupUrl: 'https://www.eia.gov/opendata/register.php',
        defaultPlan: { perMin: null, perDay: null, perMonth: null },
    },
    {
        id: 'bls',
        label: 'BLS',
        usedFor: 'Calendar · US inflation and jobs results',
        keyless: false,
        fields: [{ name: 'apiKey', label: 'Registration key', secret: true, required: true }],
        classes: [],
        defaultRestPerMin: null,
        defaultStreamSlots: null,
        signupUrl: 'https://data.bls.gov/registrationEngine/',
        defaultPlan: { perMin: null, perDay: 500, perMonth: null },
    },
    {
        id: 'fred',
        label: 'FRED',
        usedFor: 'Calendar · US results and history',
        keyless: false,
        fields: [{ name: 'apiKey', label: 'API key', secret: true, required: true }],
        classes: [],
        defaultRestPerMin: null,
        defaultStreamSlots: null,
        signupUrl: 'https://fred.stlouisfed.org/docs/api/api_key.html',
        defaultPlan: { perMin: 120, perDay: null, perMonth: null },
    },
];
export const PROVIDER_BY_ID = Object.fromEntries(PROVIDERS.map((p) => [p.id, p]));
export const isProviderId = (x) => typeof x === 'string' && x in PROVIDER_BY_ID;
export const isPriceProvider = (x) => isProviderId(x) && !INFO_ONLY.includes(x);
export const DEFAULT_ROUTING = {
    CRYPTO: ['binance', 'kraken'],
    FX: ['oanda', 'kraken', 'finnhub', 'twelvedata'],
    INDEX: ['oanda', 'finnhub', 'twelvedata'],
    COMMODITY: ['oanda', 'finnhub', 'twelvedata'],
    STOCK: ['finnhub', 'alpaca', 'twelvedata'],
};
/** A routing as stored or sent by Admin, cleaned: known providers that can price the class, no repeats. */
export function normalizeRouting(x) {
    const src = (x && typeof x === 'object' ? x : {});
    const out = {};
    for (const cls of ROUTE_CLASSES) {
        const raw = Array.isArray(src[cls]) ? src[cls] : DEFAULT_ROUTING[cls];
        const seen = new Set();
        for (const p of raw)
            if (isPriceProvider(p) && PROVIDER_BY_ID[p].classes.includes(cls))
                seen.add(p);
        out[cls] = [...seen];
    }
    return out;
}
/** Problems with a routing Admin wants to save, or null. Each class needs at least one provider. */
export function routingError(x) {
    if (!x || typeof x !== 'object')
        return 'Routing must be an object';
    const src = x;
    for (const cls of ROUTE_CLASSES) {
        const list = src[cls];
        if (!Array.isArray(list) || !list.length)
            return `${ROUTE_CLASS_LABEL[cls]} needs at least one provider`;
        for (const p of list) {
            if (!isPriceProvider(p))
                return `Unknown provider ${String(p)}`;
            if (!PROVIDER_BY_ID[p].classes.includes(cls))
                return `${PROVIDER_BY_ID[p].label} can't price ${ROUTE_CLASS_LABEL[cls]}`;
        }
        if (new Set(list).size !== list.length)
            return `${ROUTE_CLASS_LABEL[cls]} lists a provider twice`;
    }
    return null;
}
export const routeClassOf = (c) => (c === 'MEME' ? null : c);
/**
 * FX pairs Kraken trades on its spot market (free, keyless, streamed with no symbol limit). A pair it
 * stops listing is refused at subscribe and moves on down its chain by itself.
 */
export const KRAKEN_FX_PAIRS = new Set([
    'EURUSD',
    'GBPUSD',
    'USDJPY',
    'USDCAD',
    'USDCHF',
    'AUDUSD',
    'EURGBP',
    'EURJPY',
    'EURCHF',
    'EURCAD',
    'AUDJPY',
]);
/** Mask a secret for display: its last four characters. */
export function maskSecret(v) {
    return v.length <= 4 ? '••••' : `••••••••${v.slice(-4)}`;
}
/**
 * The symbol `provider` uses for an instrument: the one saved on the instrument, else one derived
 * from the platform's naming (FX EURUSD → OANDA EUR_USD, Twelve Data EUR/USD; Finnhub's
 * OANDA:XAU_USD → OANDA XAU_USD; crypto BTC → Kraken BTC/USD). Null when it can't be derived.
 */
export function providerSymbol(provider, i) {
    const saved = i.providerSymbols[provider];
    if (saved)
        return saved;
    const finnhub = i.providerSymbols.finnhub ?? (i.assetClass !== 'CRYPTO' ? i.sourceSymbol : undefined);
    switch (provider) {
        case 'binance':
            return i.assetClass === 'CRYPTO' ? i.sourceSymbol : null;
        case 'kraken':
            if (i.assetClass === 'CRYPTO')
                return `${cryptoBase(i)}/USD`;
            if (i.assetClass === 'FX' && KRAKEN_FX_PAIRS.has(i.symbol))
                return `${i.symbol.slice(0, 3)}/${i.symbol.slice(3)}`;
            return null;
        case 'oanda':
            if (finnhub?.startsWith('OANDA:'))
                return finnhub.slice(6);
            if (i.assetClass === 'FX' && /^[A-Z]{6}$/.test(i.symbol))
                return `${i.symbol.slice(0, 3)}_${i.symbol.slice(3)}`;
            return null;
        case 'alpaca':
            return i.assetClass === 'STOCK' ? (finnhub && !finnhub.includes(':') ? finnhub : i.symbol) : null;
        case 'finnhub':
            if (finnhub)
                return finnhub;
            if (i.assetClass === 'STOCK')
                return i.symbol;
            if (i.assetClass === 'FX' && /^[A-Z]{6}$/.test(i.symbol))
                return `OANDA:${i.symbol.slice(0, 3)}_${i.symbol.slice(3)}`;
            return null;
        case 'twelvedata':
            if (i.assetClass === 'STOCK')
                return finnhub && !finnhub.includes(':') ? finnhub : i.symbol;
            if (i.assetClass === 'FX' && /^[A-Z]{6}$/.test(i.symbol))
                return `${i.symbol.slice(0, 3)}/${i.symbol.slice(3)}`;
            if (i.assetClass === 'CRYPTO')
                return `${cryptoBase(i)}/USD`;
            return null;
    }
}
/** BTC from a crypto instrument (its Binance symbol BTCUSDT, or the platform symbol). */
function cryptoBase(i) {
    return i.sourceSymbol.toUpperCase().replace(/(USDT|USDC|USD)$/, '') || i.symbol;
}
//# sourceMappingURL=providers.js.map